/**
 * Demo Generator Component
 * Handles anonymous email sequence generation from homepage
 */

import DOMPurify from 'dompurify';

class DemoGenerator {
    constructor(container) {
        this.container = container;
        this.form = container.querySelector('#demo-form');
        this.progress = container.querySelector('#demo-progress');
        this.results = container.querySelector('#demo-results');
        this.error = container.querySelector('#demo-error');
        this.limit = container.querySelector('#demo-limit');
        this.emailsContainer = container.querySelector('#demo-emails');
        this.shareContainer = container.querySelector('#demo-share');

        // CTA variants for A/B testing
        this.ctaVariants = ['benefits', 'simple', 'action'];
        this.activeVariant = null;

        // Get free tier credits from data attribute (set by Django)
        const freeCredits = container.dataset.freeCredits || '50';

        // Feature tips for content rotation (shorter duration)
        this.featureTips = [
            { text: 'Customize your campaign template — set the tone, length, and goal for each email in your sequence.', duration: 4000 },
            { text: 'Track opens and clicks to see which prospects are engaging with your outreach.', duration: 4000 },
            { text: 'Pro tip: Email sequences with 3+ touchpoints get 2x more replies than single emails.', duration: 4000 },
            { text: 'Send directly through your Gmail — replies land in your inbox, not a separate tool.', duration: 4000 },
            { text: 'Add personalized URLs for each contact to track individual engagement.', duration: 4000 },
        ];

        // Sales success stories for content rotation (duration based on length)
        // Note: Aaron Levie and Collison stories are used as phase openers, not in this array
        this.salesStories = [
            { text: 'Steve Ballmer\'s sales commissions at Microsoft were so high they offered him stock instead. That stake became worth $80 billion.', duration: 8000 },
            { text: 'Salesforce\'s first enterprise deal came when Marc Benioff cold-called a friend at Cisco. That call led to a $100K contract.', duration: 8000 },
            { text: 'Before founding Nvidia, Jensen Huang was a dishwasher at Denny\'s. He credits his work ethic to serving customers under pressure.', duration: 8000 },
            { text: 'Sara Blakely sold fax machines door-to-door for 7 years, getting her business card ripped up in her face and escorted out by police. She saved $5,000 from that job and bet it all on Spanx—without a single investor—to become a self-made billionaire.', duration: 12000 },
            { text: 'Howard Schultz was rejected by 217 of 242 investors before funding Starbucks. Before that, he made 50 cold calls per day selling Xerox copiers—where he learned that rejection was "just part of the process."', duration: 10000 },
            { text: 'At 35, Joe Girard walked into a Detroit car dealership and begged for a job. That same evening he sold his first car, then borrowed $10 from the manager to buy groceries for his starving family. He went on to sell 13,001 cars—a Guinness World Record that still stands.', duration: 12000 },
            { text: 'At 12, Mark Cuban\'s father told him if he wanted basketball shoes, he needed a job. So Cuban bought garbage bags for $3 and resold them door-to-door for $6—creating what he calls "the world\'s first garbage bag subscription company."', duration: 10000 },
            { text: 'In his first 6 months selling real estate, Tom Hopkins averaged $42 per month. Down to his last $150, he spent it all on a sales seminar. He applied everything he learned and by 27 was a millionaire—in his final year, he sold 365 homes.', duration: 12000 },
        ];

        // Phase-specific intros and openers
        this.researchingIntro = { text: 'While we research, here\'s some sales inspiration...', duration: 3000 };
        this.researchingOpener = { text: 'In 2005, 20-year-old Aaron Levie was building Box from his USC dorm room. He cold-emailed Mark Cuban, who responded within hours and invested $350,000 before ever meeting him. Box is now worth $4.6 billion.', duration: 10000 };
        this.generatingIntro = { text: 'Now crafting your sequence...', duration: 2500 };
        this.generatingOpener = { text: 'When building Stripe, Patrick and John Collison invented the "Collison Installation." Instead of sending beta links, whenever someone expressed interest they\'d say "Right then, give me your laptop" and set them up on the spot.', duration: 10000 };

        // Content rotation state
        this.contentTimeout = null;
        this.contentIndex = 0;
        this.showStoryNext = true; // Alternate between stories and tips
        this.currentPhase = null; // Track phase for opener logic
        this.shownOpener = false; // Whether we've shown the first opener

        // State
        this.sessionToken = null;
        this.responseToken = null;
        this.currentJobId = null;
        this.pollInterval = null;
        this.pollTimeout = null;

        this.init();
    }

    init() {
        // Form submission
        this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

        // Reset button
        this.container.querySelector('#demo-reset')?.addEventListener('click', () => this.reset());

        // Retry button
        this.container.querySelector('#demo-retry')?.addEventListener('click', () => this.reset());

        // Copy share link
        this.container.querySelector('#copy-share-link')?.addEventListener('click', () => this.copyShareLink());

        // CTA click tracking
        this.container.querySelectorAll('.ds-demo-cta__btn').forEach(btn => {
            btn.addEventListener('click', () => this.trackCtaClick());
        });

        // Initialize session on load
        this.initSession();
    }

    selectCtaVariant() {
        // Randomly select a variant
        const randomIndex = Math.floor(Math.random() * this.ctaVariants.length);
        this.activeVariant = this.ctaVariants[randomIndex];

        // Hide all variants, show selected one
        this.container.querySelectorAll('[data-cta-variant]').forEach(el => {
            el.hidden = el.dataset.ctaVariant !== this.activeVariant;
        });

        // Track which variant was shown
        this.trackCtaShown();
    }

    trackCtaShown() {
        if (typeof posthog !== 'undefined' && this.activeVariant) {
            posthog.capture('demo_cta_shown', {
                variant: this.activeVariant
            });
        }
    }

    trackCtaClick() {
        if (typeof posthog !== 'undefined' && this.activeVariant) {
            posthog.capture('demo_cta_clicked', {
                variant: this.activeVariant
            });
        }
    }

    async initSession() {
        try {
            const response = await fetch('/api/demo/session/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                this.sessionToken = data.session_token;
                this.responseToken = data.response_token;

                // Check if user has already used all free generations
                if (data.remaining === 0) {
                    this.showLimitReached('/signup/');
                    return;
                }
            }
        } catch (err) {
            console.error('Failed to initialize demo session:', err);
        }
    }

    async handleSubmit(e) {
        e.preventDefault();

        // Ensure we have a session
        if (!this.sessionToken || !this.responseToken) {
            await this.initSession();
            if (!this.sessionToken) {
                this.showError('server_error', 'Unable to initialize session. Please refresh the page.');
                return;
            }
        }

        // Get form data
        const formData = new FormData(this.form);
        const data = {
            session_token: this.sessionToken,
            response_token: this.responseToken,
            sender_company: formData.get('sender_company'),
            target_company: formData.get('target_company'),
            target_role: formData.get('target_role'),
            value_proposition: formData.get('value_proposition')
        };

        // Store target company for phase indicator
        this.targetCompany = data.target_company;

        // Show loading state with target company name
        this.showProgress(this.targetCompany);
        this.setSubmitLoading(true);

        try {
            const response = await fetch('/api/demo/generate/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (!response.ok) {
                this.showError(result.error || 'server_error', result.message);
                return;
            }

            // Update response token for next request
            if (result.response_token) {
                this.responseToken = result.response_token;
            }

            // Start polling for results
            this.currentJobId = result.job_id;
            this.startPolling();

        } catch (err) {
            console.error('Generation request failed:', err);
            this.showError('server_error', 'Network error. Please check your connection and try again.');
        }
    }

    startPolling() {
        this.pollInterval = setInterval(() => this.pollStatus(), 1500);

        // Start content rotation
        this.startContentRotation();

        // 10 minute timeout for generation (pipeline can take 5-8 min for research + generation)
        this.pollTimeout = setTimeout(() => {
            if (this.pollInterval) {
                this.stopPolling();
                this.showError('server_error', 'Generation is taking longer than expected. Please try again.');
            }
        }, 600000);
    }

    startContentRotation() {
        // Reset content state
        this.contentIndex = 0;
        this.showStoryNext = false; // Start with tip after opener (opener is a story)
        this.currentPhase = 'researching';

        // Show intro first, then opener, then rotate
        this.showContentThen(this.researchingIntro, () => {
            this.showContent(this.researchingOpener);
        });
    }

    getNextContent() {
        let content;
        if (this.showStoryNext) {
            // Pick a random story
            const storyIndex = Math.floor(Math.random() * this.salesStories.length);
            content = this.salesStories[storyIndex];
        } else {
            // Pick a random tip
            const tipIndex = Math.floor(Math.random() * this.featureTips.length);
            content = this.featureTips[tipIndex];
        }
        // Alternate for next time
        this.showStoryNext = !this.showStoryNext;
        return content;
    }

    showNextContent() {
        const content = this.getNextContent();
        this.showContent(content);
    }

    showContentThen(content, callback) {
        // Show content, then call callback instead of continuing rotation
        const textEl = this.progress.querySelector('.ds-demo-progress__content-text');
        if (!textEl) return;

        textEl.classList.add('is-fading');

        setTimeout(() => {
            textEl.textContent = content.text;
            textEl.classList.remove('is-fading');
            this.contentTimeout = setTimeout(callback, content.duration);
        }, 200);
    }

    showContent(content) {
        const textEl = this.progress.querySelector('.ds-demo-progress__content-text');
        if (!textEl) return;

        // Fade out
        textEl.classList.add('is-fading');

        setTimeout(() => {
            textEl.textContent = content.text;
            textEl.classList.remove('is-fading');

            // Schedule next content after this one's duration
            this.contentTimeout = setTimeout(() => this.showNextContent(), content.duration);
        }, 200);
    }

    stopContentRotation() {
        if (this.contentTimeout) {
            clearTimeout(this.contentTimeout);
            this.contentTimeout = null;
        }
    }

    updatePhaseIndicator(status, companyName) {
        const phaseText = this.progress.querySelector('.ds-demo-progress__phase-text');
        if (!phaseText) return;

        if (status === 'researching') {
            phaseText.textContent = `Researching ${companyName}...`;
        } else if (status === 'generating') {
            phaseText.textContent = 'Crafting your personalized sequence...';

            // Show generating intro then opener when phase changes
            if (this.currentPhase !== 'generating') {
                this.currentPhase = 'generating';
                this.stopContentRotation();
                this.showStoryNext = false; // Next will be a tip after opener
                this.showContentThen(this.generatingIntro, () => {
                    this.showContent(this.generatingOpener);
                });
            }
        }
    }

    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.stopContentRotation();
        if (this.pollTimeout) {
            clearTimeout(this.pollTimeout);
            this.pollTimeout = null;
        }
    }

    async pollStatus() {
        if (!this.currentJobId) return;

        try {
            const response = await fetch(`/api/demo/status/${this.currentJobId}/`);
            const data = await response.json();

            // Update phase indicator with real status
            if (data.status === 'researching' || data.status === 'generating') {
                this.updatePhaseIndicator(data.status, this.targetCompany);
            }

            if (data.status === 'completed') {
                this.stopPolling();
                this.showResults(data);
            } else if (data.status === 'failed') {
                this.stopPolling();
                this.showError(data.error || 'server_error', 'Generation failed. Please try again.');
            }

        } catch (err) {
            console.error('Status poll failed:', err);
        }
    }

    showProgress(companyName) {
        this.form.hidden = true;
        this.results.hidden = true;
        this.error.hidden = true;
        this.limit.hidden = true;
        this.progress.hidden = false;

        // Set initial phase indicator
        this.updatePhaseIndicator('researching', companyName);
    }

    showResults(data) {
        this.form.hidden = true;
        this.progress.hidden = true;
        this.error.hidden = true;
        this.limit.hidden = true;
        this.results.hidden = false;
        this.setSubmitLoading(false);

        // Render context banner if available
        if (data.context) {
            this.renderContext(data.context);
        }

        // Render emails
        this.renderEmails(data.emails);

        // Select and track CTA variant for A/B test
        this.selectCtaVariant();

        // Show share link if available
        if (data.share_link) {
            const shareLinkInput = this.container.querySelector('#share-link-url');
            if (shareLinkInput) {
                shareLinkInput.value = data.share_link;
            }
            this.shareContainer.hidden = false;
        }
    }

    renderContext(context) {
        const contextEl = this.container.querySelector('#demo-context');
        if (!contextEl || !context) return;

        // Helper to safely set text content
        const setTextSafe = (selector, value) => {
            const el = this.container.querySelector(selector);
            if (el) el.textContent = value || '';
        };

        setTextSafe('#demo-context-name', context.contact_first_name);
        setTextSafe('#demo-context-role', context.target_role);
        setTextSafe('#demo-context-target', context.target_company);
        setTextSafe('#demo-context-sender', context.sender_company);

        contextEl.hidden = false;
    }

    renderEmails(emails) {
        if (!emails || !this.emailsContainer) return;

        this.emailsContainer.innerHTML = emails.map((email, index) => `
            <div class="ds-demo-email" data-email-index="${index}">
                <div class="ds-demo-email__header">
                    <span class="ds-demo-email__number">${email.sequence_number || index + 1}</span>
                    <p class="ds-demo-email__subject">${this.escapeHtml(email.subject)}</p>
                </div>
                <div class="ds-demo-email__body">
                    ${email.html_body ? DOMPurify.sanitize(email.html_body) : this.formatPlainText(email.body)}
                </div>
                <span class="ds-demo-email__toggle" onclick="this.closest('.ds-demo-email').classList.toggle('is-expanded')">
                    Show more
                </span>
            </div>
        `).join('');
    }

    showError(errorType, message) {
        let displayMessage = message;
        let showRetry = true;

        switch (errorType) {
            case 'validation_failed':
                displayMessage = message || 'Please check your input and try again.';
                showRetry = false;
                break;
            case 'rate_limited':
                displayMessage = 'Too many requests. Please wait a moment and try again.';
                break;
            case 'captcha_required':
                displayMessage = 'Please complete the CAPTCHA verification.';
                this.showCaptcha();
                showRetry = false;
                break;
            case 'server_error':
            default:
                displayMessage = 'Something went wrong. Please try again.';
        }

        // Show error panel, hide others
        this.form.hidden = true;
        this.progress.hidden = true;
        this.results.hidden = true;
        this.limit.hidden = true;
        this.error.hidden = false;
        this.setSubmitLoading(false);

        // Display error with appropriate UI
        this.error.innerHTML = `
            <div class="ds-demo-error">
                <p>${this.escapeHtml(displayMessage)}</p>
                ${showRetry ? '<button onclick="this.closest(\'.ds-demo-generator\').demoGenerator.retry()">Try Again</button>' : ''}
            </div>
        `;
    }

    showLimitReached(signupUrl) {
        this.form.hidden = true;
        this.progress.hidden = true;
        this.results.hidden = true;
        this.error.hidden = true;
        this.limit.hidden = false;
        this.setSubmitLoading(false);

        // Update the CTA link if a custom signup URL is provided
        if (signupUrl) {
            const ctaLink = this.container.querySelector('#demo-limit-cta');
            if (ctaLink) {
                ctaLink.href = signupUrl;
            }
        }

        // Track limit reached event
        if (typeof posthog !== 'undefined') {
            posthog.capture('demo_limit_reached');
        }
    }

    reset() {
        this.stopPolling();
        this.currentJobId = null;

        // Reset form visibility
        this.form.hidden = false;
        this.progress.hidden = true;
        this.results.hidden = true;
        this.error.hidden = true;
        this.limit.hidden = true;
        this.shareContainer.hidden = true;

        // Hide context banner
        const contextEl = this.container.querySelector('#demo-context');
        if (contextEl) {
            contextEl.hidden = true;
        }

        // Clear emails
        if (this.emailsContainer) {
            this.emailsContainer.innerHTML = '';
        }

        // Reset retry button
        const retryBtn = this.container.querySelector('#demo-retry');
        if (retryBtn) {
            retryBtn.textContent = 'Try again';
            retryBtn.onclick = () => this.reset();
        }

        // Re-init session for fresh attempt
        this.initSession();
    }

    setSubmitLoading(isLoading) {
        const submitBtn = this.form.querySelector('.ds-demo-form__submit');
        if (submitBtn) {
            submitBtn.classList.toggle('is-loading', isLoading);
            submitBtn.disabled = isLoading;

            const textSpan = submitBtn.querySelector('.ds-btn__text');
            const spinnerSpan = submitBtn.querySelector('.ds-btn__spinner');

            if (textSpan) textSpan.hidden = isLoading;
            if (spinnerSpan) spinnerSpan.hidden = !isLoading;
        }
    }

    async copyShareLink() {
        const input = this.container.querySelector('#share-link-url');
        const btn = this.container.querySelector('#copy-share-link');
        if (!input || !btn) return;

        const originalText = btn.textContent;

        try {
            await navigator.clipboard.writeText(input.value);
            btn.textContent = 'Copied!';
        } catch (err) {
            // Fallback: select text for manual copy
            input.select();
            btn.textContent = 'Press Ctrl+C';
        }

        setTimeout(() => btn.textContent = originalText, 2000);
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    formatPlainText(text) {
        if (!text) return '';
        return text.split('\n').map(p => `<p>${this.escapeHtml(p)}</p>`).join('');
    }
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('demo-generator');
    if (container) {
        window.demoGenerator = new DemoGenerator(container);
    }
});

export default DemoGenerator;
